import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SlideOver from "@/components/SlideOver";
import FeedItem from "@/components/FeedItem";
import { Rss, UserPlus, ImagePlus, Loader2, Send, Users } from "lucide-react";

const VibeFeed = () => {
  const [feed, setFeed] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [msg, setMsg] = useState("");
  const [vibe, setVibe] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [f, c] = await Promise.all([api.get("/feed"), api.get("/connections")]);
    setFeed(f.data); setConnections(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) return;
    setInviting(true); setMsg("");
    try {
      const r = await api.post("/connections/invite", { email });
      setMsg(r.data.status === "connected" ? `Connected with ${r.data.connection.name}!` : r.data.note);
      setEmail("");
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not invite.");
    } finally { setInviting(false); }
  };

  const postVibe = async (image_base64) => {
    if (!vibe.trim() && !image_base64) return;
    await api.post("/boutique/vibe", { type: image_base64 ? "image" : "thought", content: vibe, image_base64: image_base64 || "" });
    setVibe("");
    load();
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
    await postVibe(b64);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">The Vibe Feed <Rss className="w-7 h-7 text-primary" /></h1>
          <p className="text-muted-foreground">Private, invitation-only. Curated thoughtfulness from your Circle — no ads, no noise.</p>
        </div>
        <button data-testid="connect-btn" onClick={() => setConnectOpen(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 py-2.5 hover:opacity-90"><UserPlus className="w-4 h-4" /> Invite to Circle</button>
      </div>

      {/* Composer */}
      <div className="surface rounded-[1.5rem] p-5 mb-8">
        <div className="flex gap-2">
          <input data-testid="feed-vibe-input" value={vibe} onChange={(e) => setVibe(e.target.value)}
            className="flex-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Share a vibe with your circle…" />
          <button onClick={() => fileRef.current?.click()} className="surface rounded-2xl px-4 hover:border-primary/40" data-testid="feed-image-btn"><ImagePlus className="w-4 h-4" /></button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} />
          <button data-testid="feed-post-btn" onClick={() => postVibe()} className="bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 hover:opacity-90 flex items-center gap-2"><Send className="w-4 h-4" /></button>
        </div>
      </div>

      {connections.length > 0 && (
        <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground">
          <Users className="w-4 h-4" /> Connected: {connections.map((c) => c.name).join(", ")}
        </div>
      )}

      {loading ? (
        <div className="text-center py-24 text-muted-foreground">Loading your feed…</div>
      ) : feed.length === 0 ? (
        <div className="surface rounded-[2rem] p-16 text-center">
          <Rss className="w-12 h-12 mx-auto mb-4 text-primary/60" />
          <h3 className="font-serif text-2xl mb-2">Your feed is quiet</h3>
          <p className="text-muted-foreground">Invite someone to your Circle, or post your first Vibe above.</p>
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="vibe-feed">
          {feed.map((v) => <FeedItem key={v.id} vibe={v} />)}
        </div>
      )}

      <SlideOver open={connectOpen} onClose={() => setConnectOpen(false)} title="Invite to your Circle" subtitle="Private & invitation-only">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Enter a friend&apos;s email. If they&apos;re on GIFTED, you&apos;ll connect instantly and see each other&apos;s Vibes.</p>
          <input data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email"
            className="w-full bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="friend@example.com" />
          <button data-testid="invite-submit" onClick={invite} disabled={inviting}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3.5 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
            {inviting ? <><Loader2 className="w-4 h-4 animate-spin" /> Inviting…</> : <><UserPlus className="w-4 h-4" /> Send Invite</>}
          </button>
          {msg && <div data-testid="invite-msg" className="text-sm text-center text-secondary">{msg}</div>}
          <p className="text-[11px] text-center text-muted-foreground/70">All Boutique data stays inside the Gifted Estate. No external scraping.</p>
        </div>
      </SlideOver>
    </AppShell>
  );
};

export default VibeFeed;
